import React, {
	Dispatch,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { isBoolean } from "lodash";
import styled from "styled-components";

import { SIP } from "../../../../services";
import { useModelSubscribe } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";
import { StyledP, StyledRow, StyledColumn } from "../..";

const EqualWidthColumn = styled(StyledColumn)`
	flex: 1;
`;

const MultiSelectSIPWithModalBase: React.FC<
	MultiSelectSIPWithModalBase.Props
> = ({
	value,
	onChange,
	disabled,
	titleText = "",
	required,
	modalSetting,
	error = "",
	sort,
	showHeadTitle = false,
	type,
	...props
}) => {
	const { t } = useTranslation();

	const options = useMemo(() => {
		const result: SIP.SubscribeOptions = {};

		// if (type === "soft-phone") result.isSoftPhone = true;
		// else if (type === "hard-phone") result.isSoftPhone = false;

		return result;
	}, []);

	const subscribe = useModelSubscribe(options, SIP);

	const items = useMemo(() => subscribe.models, [subscribe.models]);

	const [errorInternal, setErrorInternal] = useState<string | boolean>("");

	useEffect(() => {
		setErrorInternal(error);
	}, [error]);

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			items
				.filter((item) => {
					if (
						(type === "soft-phone" && item.isSoftPhone) ||
						(type === "hard-phone" && !item.isSoftPhone)
					)
						return true;

					return false;
				})
				.map((item) => ({
					key: item.id,
					label: item.value,
					value: item.id,
					name: item.value,
					data: item,
				})),
		[items, type],
	);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			selectOptions.filter((item) =>
				value.find((data) => data === item.value),
			),

		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue) => {
			setErrorInternal("");
			const Ids = newValue.map((item) => item.value);
			onChange(Ids);
		},
		[onChange],
	);

	const texts = useMemo(
		() => ({
			title: t(titleText),
			allText: t(["all_sips"]),
			allActiveText: t(["all_active_sips"]),
		}),
		[t, titleText],
	);

	const valuesOptions = useMemo(() => {
		const arr = Array.isArray(selectOptions) ? selectOptions : [];
		const options = [...arr].filter((item) => {
			if (!isBoolean(item.active)) return true;
			return item.active === true;
		});

		const actives = [...arr].filter(
			(item) => isBoolean(item.active) && item.active === true,
		);

		const notActives = [...arr].filter(
			(item) => isBoolean(item.active) && item.active === false,
		);

		return {
			optionsLength: options.length,
			valueLength: selected.length,
			actives,
			notActives,
		};
	}, [selectOptions, selected]);

	return (
		<StyledRow gap="7px" alignItems="center">
			<EqualWidthColumn gap="7px">
				{showHeadTitle && <StyledP>{t(titleText) ?? ""}</StyledP>}
				<MultiSelectWithModal
					error={errorInternal}
					disabled={disabled}
					value={selected}
					options={selectOptions}
					actives={valuesOptions.actives}
					notActives={valuesOptions.notActives}
					title={texts.title}
					allText={texts.allText}
					allActiveText={texts.allActiveText}
					optionsLength={valuesOptions.optionsLength}
					valueLength={valuesOptions.valueLength}
					onChange={selectOnChange}
					required={required}
					pullUpItemInsideArray
					hiddenButton={false}
					showSelectAll
					modalSetting={modalSetting}
					sort={{
						show: true,
						active: true,
						...(sort || {}),
					}}
					{...props}
				/>
			</EqualWidthColumn>
		</StyledRow>
	);
};

export const MultiSelectSIPWithModal = memo(MultiSelectSIPWithModalBase);

declare namespace MultiSelectSIPWithModalBase {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText?: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		error?: string | boolean;
		showHeadTitle?: boolean;
		type?: "soft-phone" | "hard-phone";
	}

	type Value = number[];
}

export default MultiSelectSIPWithModalBase;
